import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useDispatch } from 'react-redux'
import useReactRouter from 'use-react-router'
import DocumentMeta from 'core/components/DocumentMeta'
import Button from 'core/elements/button/Button'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import { clientActions } from 'core/client/clientReducers'
import { getApplicationCredential } from './actions'
import { ApplicationCredential, AccessRuleMethod } from './model'
import Progress from 'core/components/progress/Progress'
import Card from 'core/elements/card'
import { GridViewColumn } from 'core/elements/grid/Grid'
import Grid from 'core/elements/grid/Grid'
import { Chip } from '@material-ui/core'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { APPLICATION_CREDENTIALS_ROLE_LABEL } from './constant'
import { whitelistedRoles } from 'app/plugins/account/components/userManagement/roles/selectors'

interface AggregatedAccessRule {
  id: string
  service: string
  path: string
  methods: AccessRuleMethod[]
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: theme.spacing(3),
  },
  overviewCard: {
    padding: theme.spacing(3),
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  customCardHeader: {
    display: 'flex',
    paddingBottom: '16px ',
    gap: 8,
    alignItems: 'center',
  },
  iconContainer: {
    background: theme.palette.blue[900],
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    width: 24,
  },
  icon: {
    fontSize: 14,
    color: theme.palette.grey['000'],
  },
  headerText: {
    color: theme.palette.grey[500],
  },
  overviewGrid: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  label: {
    color: theme.palette.grey[500],
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'uppercase',
  },
  value: {
    color: theme.palette.grey[700],
    fontSize: 14,
    fontWeight: 600,
    wordBreak: 'break-all',
  },
  uuidValue: {
    color: theme.palette.grey[700],
    fontSize: 21,
    fontWeight: 'bold',
    wordBreak: 'break-all',
  },
  overviewSection: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  accessRulesCard: {
    padding: theme.spacing(3),
  },
  methodChip: {
    borderRadius: '4px !important',
    backgroundColor: '#EEEEF2 !important',
    color: `${theme.palette.grey[700]} !important`,
  },
  pathMethodsCell: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  pathName: {
    fontWeight: 600,
  },
  pathMethodsSeparator: {
    margin: 0,
  },
  methodGet: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  methodPost: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  methodPut: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  methodDelete: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  methodPatch: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
  },
}))

const getMethodClass = (method: string, classes: ReturnType<typeof useStyles>) => {
  switch (method.toUpperCase()) {
    case 'GET':
      return classes.methodGet
    case 'POST':
      return classes.methodPost
    case 'PUT':
      return classes.methodPut
    case 'DELETE':
      return classes.methodDelete
    case 'PATCH':
      return classes.methodPatch
    default:
      return ''
  }
}

export default function ApplicationCredentialDetailsPage() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id } = match.params as { id: string }

  const [loading, setLoading] = useState(true)
  const [credential, setCredential] = useState<ApplicationCredential | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadCredential = async (forceReload = false) => {
    setLoading(true)
    setError(null)
    try {
      const data = await getApplicationCredential(id)
      setCredential(data.application_credential)
    } catch (err) {
      setError(err?.message || 'Failed to load application credential')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCredential()
  }, [id])

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        id: credential?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [credential?.name, id])

  const aggregatedAccessRules: AggregatedAccessRule[] = useMemo(() => {
    if (!credential?.access_rules || credential.access_rules.length === 0) {
      return []
    }

    const groupedRules = new Map<string, AggregatedAccessRule>()

    credential.access_rules.forEach((rule) => {
      const key = `${rule.service}|${rule.path}`
      const existing = groupedRules.get(key)

      if (existing) {
        if (!existing.methods.includes(rule.method)) {
          existing.methods.push(rule.method)
        }
      } else {
        groupedRules.set(key, {
          id: key,
          service: rule.service,
          path: rule.path,
          methods: [rule.method],
        })
      }
    })

    return Array.from(groupedRules.values())
  }, [credential?.access_rules])

  const accessRulesColumns: GridViewColumn<AggregatedAccessRule>[] = useMemo(
    () => [
      {
        key: 'service',
        label: 'Service Name',
        width: 'medium',
      },
      {
        key: 'path',
        label: 'Paths & Methods Pairs',
        render: (path: string, row: AggregatedAccessRule) => (
          <div className={classes.pathMethodsCell}>
            <span className={classes.pathName}>{path}</span>
            <span className={classes.pathMethodsSeparator}>-</span>
            {row.methods.map((method) => (
              <Chip
                key={method}
                label={method}
                size="small"
                className={`${classes.methodChip} ${getMethodClass(method, classes)}`}
              />
            ))}
          </div>
        ),
      },
    ],
    [classes],
  )

  const formattedExpiration = useMemo(() => {
    if (!credential?.expires_at) return 'Never'
    // OpenStack API returns datetime without Z suffix, treat as UTC
    const expiresAt = credential.expires_at
    const utcString = expiresAt.endsWith('Z') ? expiresAt : `${expiresAt}Z`
    const date = new Date(utcString)
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }, [credential?.expires_at])

  return (
    <>
      <DocumentMeta title="Application Credential Details" breadcrumbs />
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => loadCredential(true)}
          loading={loading}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>

      <Progress loading={loading} message="Loading application credential...">
        {error ? (
          <Text variant="body1" color="error">
            {error}
          </Text>
        ) : credential ? (
          <div className={classes.container}>
            <div className={classes.overviewCard}>
              <div className={classes.customCardHeader}>
                <div className={classes.iconContainer}>
                  <FontAwesomeIcon className={classes.icon}>key</FontAwesomeIcon>
                </div>
                <Text variant="body2" className={classes.headerText}>
                  CONFIGURATION OVERVIEW
                </Text>
              </div>
              <div className={classes.overviewGrid}>
                <Text variant="h4" className={classes.uuidValue}>
                  UUID- {credential.id}
                </Text>

                <div className={classes.overviewSection}>
                  <Text variant="caption2" className={classes.label}>
                    Role
                  </Text>
                  <Text variant="caption1" className={classes.value}>
                    {credential.roles
                      ?.filter((role) => whitelistedRoles.includes(role.name))
                      .map((role) => APPLICATION_CREDENTIALS_ROLE_LABEL[role.name])
                      .join(', ') || 'N/A'}
                  </Text>
                </div>

                <div className={classes.overviewSection}>
                  <Text variant="caption2" className={classes.label}>
                    Expiration
                  </Text>
                  <Text variant="caption1" className={classes.value}>
                    {formattedExpiration}
                  </Text>
                </div>

                <div className={classes.overviewSection}>
                  <Text variant="caption2" className={classes.label}>
                    Description
                  </Text>
                  <Text variant="caption1" className={classes.value}>
                    {credential.description || 'N/A'}
                  </Text>
                </div>

                <div className={classes.overviewSection}>
                  <Text variant="caption2" className={classes.label}>
                    Restricted
                  </Text>
                  <Text variant="caption1" className={classes.value}>
                    {credential.unrestricted ? 'False' : 'True'}
                  </Text>
                </div>
              </div>
            </div>

            {aggregatedAccessRules.length > 0 ? (
              <Grid
                uniqueIdentifier="id"
                data={aggregatedAccessRules}
                columns={accessRulesColumns}
                label="Access Rules"
              />
            ) : (
              <Card className={classes.accessRulesCard}>
                <Text variant="body2" color="textSecondary">
                  No access rules defined. This credential has full access based on the assigned
                  role.
                </Text>
              </Card>
            )}
          </div>
        ) : null}
      </Progress>
    </>
  )
}
